import { actionCreator, ActionReturnType } from "@itk/typescript-utils";
import { TConfiguratorViewSettings } from "src/utils/types/identity-settings-types";

// Sentinel ids representing the two entity-label tag groups — Configuration-level and
// Product(line item)-level labels are distinct domain entities — plus the product type, as
// selectable, reorderable items alongside real attribute ids. There's no separate "show labels" toggle anymore.
export const VIEW_SETTINGS_CONFIGURATION_LABELS_ITEM_ID = "configurationLabels";
export const VIEW_SETTINGS_PRODUCT_LABELS_ITEM_ID = "productLabels";
export const VIEW_SETTINGS_PRODUCT_TYPE_ITEM_ID = "productType";

export enum Action {
  SetDraftSelectedItemIds = "@@ITK:SRC:PAGES:CONFIGURATOR:COMPONENTS:VIEW-SETTINGS:VIEW-SETTINGS-STORE.TS:ACTION:SETDRAFTSELECTEDITEMIDS",
  ApplyDraft = "@@ITK:SRC:PAGES:CONFIGURATOR:COMPONENTS:VIEW-SETTINGS:VIEW-SETTINGS-STORE.TS:ACTION:APPLYDRAFT",
  ClearAll = "@@ITK:SRC:PAGES:CONFIGURATOR:COMPONENTS:VIEW-SETTINGS:VIEW-SETTINGS-STORE.TS:ACTION:CLEARALL",
  LoadPersistedRequest = "@@ITK:SRC:PAGES:CONFIGURATOR:COMPONENTS:VIEW-SETTINGS:VIEW-SETTINGS-STORE.TS:ACTION:LOADPERSISTEDREQUEST",
  LoadPersistedSuccess = "@@ITK:SRC:PAGES:CONFIGURATOR:COMPONENTS:VIEW-SETTINGS:VIEW-SETTINGS-STORE.TS:ACTION:LOADPERSISTEDSUCCESS",

  Clear = "@@ITK:SRC:PAGES:CONFIGURATOR:COMPONENTS:VIEW-SETTINGS:VIEW-SETTINGS-STORE.TS:ACTION:CLEAR"
}

type TViewSettingsSelection = TConfiguratorViewSettings;

type IViewSettingsState = {
  applied: TViewSettingsSelection;
  draft: TViewSettingsSelection;
};

const getDefaultSelection = (): TViewSettingsSelection => ({ selectedItemIds: [] });

const initialState: IViewSettingsState = {
  applied: getDefaultSelection(),
  draft: getDefaultSelection()
};

export const viewSettingsActions = {
  // Order-significant — drives both which rows' chips render and the on-screen chip order.
  setDraftSelectedItemIds: (selectedItemIds: string[]) => actionCreator(Action.SetDraftSelectedItemIds, { selectedItemIds }),
  // Commits the current draft as the applied selection row rendering reads
  applyDraft: () => actionCreator(Action.ApplyDraft),
  // Resets both draft and applied to empty — mirrors Filter's handleReset, which clears the draft AND immediately re-applies empty
  clearAll: () => actionCreator(Action.ClearAll),
  loadPersistedRequest: (projectId: string) => actionCreator(Action.LoadPersistedRequest, { projectId }),
  loadPersistedSuccess: (selectedItemIds: string[]) => actionCreator(Action.LoadPersistedSuccess, { selectedItemIds }),

  clear: () => actionCreator(Action.Clear)
};

export const viewSettingsReducer = (state = initialState, action: ActionReturnType<typeof viewSettingsActions>): IViewSettingsState => {
  switch (action.type) {
    case Action.SetDraftSelectedItemIds:
      return { ...state, draft: { ...state.draft, selectedItemIds: action.payload.selectedItemIds } };
    case Action.ApplyDraft:
      return { ...state, applied: state.draft };
    case Action.ClearAll:
      return { ...state, draft: getDefaultSelection(), applied: getDefaultSelection() };
    case Action.LoadPersistedSuccess:
      return {
        ...state,
        applied: { selectedItemIds: action.payload.selectedItemIds },
        draft: { selectedItemIds: action.payload.selectedItemIds }
      };

    case Action.Clear:
      return initialState;
    default:
      return state;
  }
};
